import { IMPORT_RUN_CANCELLABLE_STATUSES } from '@enp/domain/api';
import { formatCount } from '@enp/domain/format';
import { Button, EmptyState, PageHeader, Skeleton } from '@enp/ui';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight, Ban, BookOpen, FileSearch, Loader2, Play } from 'lucide-react';
import type { ReactNode } from 'react';
import {
  ImportsBreadcrumb,
  isOutdated,
  Note,
  RunStatusBadge,
  runLabel,
  useImportPermissions,
  useSourceName,
  useStartRun,
} from '../../../../../imports/components.js';
import {
  connectionsQuery,
  rulesQuery,
  runQuery,
  runsQuery,
} from '../../../../../imports/queries.js';
import { ReportView } from '../../../../../imports/report.js';
import {
  CancelRunButton,
  FailureView,
  LoadButton,
  LoadGateNote,
  LoadResult,
  ProgressView,
  RecheckButton,
  RulesOutdatedNote,
} from '../../../../../imports/run-parts.js';
import { ApiError } from '../../../../../lib/api.js';
import { AccountDate, useAccountTimeZone } from '../../../../../shell/account-date.js';
import { useDocumentTitle } from '../../../../../shell/page.js';
import { ErrorPanel } from '../../../../../shell/route-states.js';

export const Route = createFileRoute('/_app/imports/runs/$runId/')({
  component: RunPage,
});

function RunSkeleton() {
  return (
    <div role="status" aria-live="polite">
      <h1 className="sr-only">Loading import</h1>
      <span className="sr-only">Loading import…</span>
      <div className="flex flex-col gap-2 pb-6">
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-8 w-80" />
        <Skeleton className="h-5 w-64" />
      </div>
      <Skeleton className="mb-6 h-36 w-full rounded-lg" />
      <div className="grid gap-6 min-[1360px]:grid-cols-[minmax(0,1fr)_22rem]">
        <Skeleton className="h-96 rounded-lg" />
        <Skeleton className="h-64 rounded-lg" />
      </div>
    </div>
  );
}

function BackToImports() {
  return (
    <Button variant="secondary" asChild>
      <Link to="/imports">
        <ArrowLeft aria-hidden />
        Back to imports
      </Link>
    </Button>
  );
}

function RunPage() {
  const { runId } = Route.useParams();
  const run = useQuery(runQuery(runId));
  const rules = useQuery(rulesQuery());
  const runs = useInfiniteQuery(runsQuery());
  const connections = useQuery(connectionsQuery());
  const sourceName = useSourceName(run.data?.connector_kind);
  const { canEdit } = useImportPermissions();
  const { start, pending: starting } = useStartRun();
  const timeZone = useAccountTimeZone();
  useDocumentTitle(run.data ? runLabel(run.data, timeZone) : 'Import');

  if (run.isPending) return <RunSkeleton />;
  if (run.isError) {
    if (run.error instanceof ApiError && run.error.status === 404) {
      return (
        <div className="max-w-2xl">
          <EmptyState
            icon={FileSearch}
            titleAs="h1"
            title="Import not found"
            description="There’s no import at this address. It may belong to another account, or the link may be mistyped."
            action={<BackToImports />}
          />
        </div>
      );
    }
    return (
      <div className="max-w-2xl">
        <ErrorPanel
          error={run.error}
          what="This import"
          onRetry={() => void run.refetch()}
          extraAction={<BackToImports />}
        />
      </div>
    );
  }

  const r = run.data;
  const report = r.report;
  const cancellable = (IMPORT_RUN_CANCELLABLE_STATUSES as readonly string[]).includes(r.status);
  // After decisions, the run re-plans; the previous report stays on screen meanwhile.
  const rechecking = r.status === 'planning' && report !== null;
  // Rules saved (or decisions made on another run) after this run was checked: a load is
  // refused (409 rules_outdated) until it is re-checked.
  const rulesRevision = rules.data?.revision;
  const outdated = isOutdated(r, rulesRevision);

  const actions: ReactNode[] = [];
  if (canEdit && cancellable) actions.push(<CancelRunButton key="cancel" run={r} />);
  if (r.status === 'awaiting_review') {
    actions.push(
      <Button key="review" asChild>
        <Link to="/imports/runs/$runId/review" params={{ runId: r.id }}>
          Decide {formatCount(r.review_items ?? 0)} {r.review_items === 1 ? 'item' : 'items'}
          <ArrowRight aria-hidden />
        </Link>
      </Button>,
    );
  }
  if (r.status === 'ready' && canEdit) {
    // Out of date: the only way on is to check it again (a load is refused, UI1A-1).
    actions.push(
      outdated ? (
        <RecheckButton key="recheck" run={r} />
      ) : (
        <LoadButton key="load" run={r} sourceName={sourceName} />
      ),
    );
  }
  if (r.status === 'awaiting_review' && outdated && canEdit) {
    actions.unshift(<RecheckButton key="recheck" run={r} />);
  }
  if (r.status === 'loaded') {
    actions.push(
      <Button key="catalogue" asChild>
        <Link to="/catalogue">
          <BookOpen aria-hidden />
          Open the catalogue
        </Link>
      </Button>,
    );
  }
  if ((r.status === 'cancelled' || r.status === 'loaded') && canEdit) {
    actions.unshift(
      <Button
        key="again"
        variant="secondary"
        icon={<Play aria-hidden />}
        loading={starting}
        onClick={() => start(r.connection_id)}
      >
        {starting ? 'Starting…' : 'Import again'}
      </Button>,
    );
  }

  let body: ReactNode;
  if (r.status === 'failed') {
    body = (
      <div className="max-w-3xl">
        <FailureView run={r} sourceName={sourceName} />
      </div>
    );
  } else if (r.status === 'cancelled') {
    const superseded = /^Superseded by run ([0-9a-f-]{36})$/.exec(r.error ?? '');
    body = (
      <div className="max-w-2xl">
        <EmptyState
          compact
          icon={Ban}
          title="This import was cancelled"
          description={
            superseded
              ? 'A newer import of the same source replaced it before it was loaded. Nothing from this one was loaded.'
              : 'It stopped before anything was loaded into the catalogue.'
          }
          action={
            superseded ? (
              <Button asChild>
                <Link to="/imports/runs/$runId" params={{ runId: superseded[1] as string }}>
                  Open the newer import
                  <ArrowRight aria-hidden />
                </Link>
              </Button>
            ) : (
              <BackToImports />
            )
          }
        />
      </div>
    );
  } else if (report && r.status !== 'loading') {
    body = (
      <div className="flex flex-col gap-6">
        {rechecking && (
          <Note icon={Loader2} tone="info" role="status">
            <p className="font-medium">Re-checking with your decisions…</p>
            <p className="text-muted">The report below updates by itself in a moment.</p>
          </Note>
        )}
        {outdated && rulesRevision !== undefined && (
          <RulesOutdatedNote run={r} rulesRevision={rulesRevision} />
        )}
        {!outdated && <LoadGateNote run={r} />}
        {r.status === 'loaded' && <LoadResult run={r} />}
        <ReportView
          run={r}
          report={report}
          sourceName={sourceName}
          outdated={outdated}
          rechecking={rechecking}
        />
      </div>
    );
  } else {
    // The last import of this source says about how many items there are to read.
    const previous = runs.data?.pages
      .flatMap((p) => p.items)
      .find((x) => x.id !== r.id && x.connection_id === r.connection_id && x.counts.extract);
    body = (
      <div className="max-w-2xl">
        <ProgressView
          run={r}
          sourceName={sourceName}
          expectedItems={previous?.counts.extract?.items}
          lastRequests={previous?.counts.extract?.requests}
          budget={connections.data?.connections.find((c) => c.id === r.connection_id)?.rate_budget}
        />
      </div>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow={<ImportsBreadcrumb page={runLabel(r, timeZone)} />}
        title={`Import from ${sourceName}`}
        description={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <RunStatusBadge run={r} outdated={outdated} />
            <span className="text-sm text-muted">
              Started <AccountDate value={r.started_at} withTime />
              {r.counts.extract && <> · {formatCount(r.counts.extract.items)} items read</>}
            </span>
          </span>
        }
        actions={actions.length > 0 ? <>{actions}</> : undefined}
      />
      {body}
    </>
  );
}
